/**
 * Endpoints de **Finanzas → Exportar Concar** (`/finanzas/exportar-concar`).
 *
 * Son cinco llamadas: el resumen del período (para saber cuántos asientos y libros
 * salen antes de descargar), el Excel de asientos o de plantilla y la gestión de los
 * subdiarios que CONCAR usa para numerar los libros.
 */

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { ListaSubdiarios, ResumenConcar, SubdiarioConcar } from './exportar-concar.types';

export const concarKeys = {
  todo: ['finanzas', 'exportar-concar'] as const,
  resumen: (periodo: string) => ['finanzas', 'exportar-concar', 'resumen', periodo] as const,
  subdiarios: ['finanzas', 'exportar-concar', 'subdiarios'] as const,
};

export const concarApi = {
  /** Excel con los asientos del período (o la plantilla vacía). */
  urlExcel: (periodo: string, conDatos: boolean) =>
    `/api/finanzas/exportar-concar/excel?periodo=${encodeURIComponent(periodo)}&con_datos=${
      conDatos ? 'si' : 'no'
    }`,

  resumen: (periodo: string, signal?: AbortSignal) =>
    apiClient.get<ResumenConcar>(
      `/api/finanzas/exportar-concar/resumen?periodo=${encodeURIComponent(periodo)}`,
      { signal },
    ),

  subdiarios: (signal?: AbortSignal) =>
    apiClient.get<ListaSubdiarios>('/api/finanzas/exportar-concar/subdiarios', { signal }),

  guardarSubdiarios: (subdiarios: SubdiarioConcar[]) =>
    apiClient.post<{ subdiarios: number; message: string }>(
      '/api/finanzas/exportar-concar/subdiarios',
      { body: { subdiarios } },
    ),

  borrarSubdiario: (id: number) =>
    apiClient.delete<{ id: number; message: string }>(
      `/api/finanzas/exportar-concar/subdiarios/${id}`,
    ),
};

/** Resumen del período (comprobantes, asientos y libros). */
export function useResumenConcar(periodo: string) {
  return useQuery({
    queryKey: concarKeys.resumen(periodo),
    queryFn: ({ signal }) => concarApi.resumen(periodo, signal),
    enabled: periodo.length === 7,
    placeholderData: (anterior) => anterior,
  });
}

/** Subdiarios configurados. */
export function useSubdiariosConcar(habilitado = true) {
  return useQuery({
    queryKey: concarKeys.subdiarios,
    queryFn: ({ signal }) => concarApi.subdiarios(signal),
    enabled: habilitado,
    placeholderData: (anterior) => anterior,
  });
}
